import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomSelect } from '../CustomFormElements';
import DateRangeSelector from '../DateRangeSelector';
import useLatestRequest from '../../Hooks/useLatestRequest';
import { useAgentApi } from '../../Services/AgentApiProvider';
import { AgentMetricsResponse } from '../../Services/AgentApiProviderTypes';
import { formatCount, METRIC_CHARTS, OUTCOMES, total } from './agentMetricCharts';
import { LineChart, LineSeries, lineKeyClass } from './LineChart';
import './AgentMetrics.css';

// one line per agent, and the chart palette has eight colors
export const MAX_AGENTS = 8;

interface Period {
  start: string;
  end: string | null;
}

const selectionKey = (organizationId: string) => `org_agent_metrics_${organizationId}`;

// null: nothing chosen yet, the backend picks the agents
const loadSelection = (organizationId: string): string[] | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(selectionKey(organizationId)) || 'null');
    return Array.isArray(stored)
      ? stored.filter((id) => typeof id === 'string').slice(0, MAX_AGENTS)
      : null;
  } catch {
    return null;
  }
};

const saveSelection = (organizationId: string, ids: string[]) => {
  try {
    localStorage.setItem(selectionKey(organizationId), JSON.stringify(ids));
  } catch {}
};

/**
 * A color slot per agent that it keeps while it stays selected: removing one
 * agent never repaints the others, and a new one takes the lowest free slot.
 */
export const assignSlots = (previous: Map<string, number>, ids: string[]): Map<string, number> => {
  const next = new Map<string, number>();
  ids.forEach((id) => {
    const slot = previous.get(id);
    if (slot !== undefined) next.set(id, slot);
  });

  const used = new Set(next.values());
  ids.forEach((id) => {
    if (next.has(id)) return;
    let slot = 1;
    while (used.has(slot)) slot += 1;
    next.set(id, slot);
    used.add(slot);
  });

  return next;
};

const useStableSlots = (ids: string[]): Map<string, number> => {
  const slots = useRef(new Map<string, number>());
  const key = ids.join(',');

  return useMemo(() => {
    slots.current = assignSlots(slots.current, ids);
    return slots.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};

interface AgentMetricsPanelProps {
  organizationId: string;
}

export default function AgentMetricsPanel({ organizationId }: AgentMetricsPanelProps) {
  const { getAgents, getAgentMetrics } = useAgentApi();
  const beginRequest = useLatestRequest();

  const [agentOptions, setAgentOptions] = useState<{ value: string; label: string }[]>([]);
  const [period, setPeriod] = useState<Period | null>(null);
  const [selection, setSelection] = useState<string[] | null>(() => loadSelection(organizationId));
  const [metrics, setMetrics] = useState<AgentMetricsResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    let isCurrent = true;

    getAgents(0, 1000)
      .then((response) => {
        if (isCurrent) {
          setAgentOptions(response.items.map((agent) => ({ value: agent.key, label: agent.name })));
        }
      })
      .catch(() => {});

    return () => {
      isCurrent = false;
    };
  }, [getAgents]);

  useEffect(() => {
    // an explicitly emptied selection has nothing to show; no ids would mean "pick for me"
    if (!period || selection?.length === 0) return;

    const isLatest = beginRequest();
    setStatus('loading');

    getAgentMetrics(period.start, period.end, selection)
      .then((response) => {
        if (!isLatest()) return;
        setMetrics(response);
        setStatus('ready');
      })
      .catch(() => {
        if (isLatest()) setStatus('failed');
      });
  }, [period, selection, getAgentMetrics, beginRequest]);

  const handlePeriodChange = useCallback(
    (range: { start_date: string; end_date: string | null }) => {
      setPeriod({ start: range.start_date, end: range.end_date });
    },
    [],
  );

  const handleSelectionChange = (value: string | string[]) => {
    const ids = (Array.isArray(value) ? value : []).slice(0, MAX_AGENTS);
    setSelection(ids);
    saveSelection(organizationId, ids);
  };

  const shownAgents = useMemo(() => metrics?.agents ?? [], [metrics]);
  const slots = useStableSlots(shownAgents.map((agent) => agent.id));

  // agents shown by the backend stay selectable even if the agent list failed to load
  const options = useMemo(() => {
    const known = new Set(agentOptions.map((option) => option.value));
    return [
      ...agentOptions,
      ...shownAgents
        .filter((agent) => !known.has(agent.id))
        .map((agent) => ({ value: agent.id, label: agent.name })),
    ];
  }, [agentOptions, shownAgents]);

  // a remembered agent that has been deleted since is not offered any more
  const selectedIds = selection
    ? selection.filter((id) => options.some((option) => option.value === id))
    : shownAgents.map((agent) => agent.id);

  const renderBody = () => {
    if (selection?.length === 0) {
      return <p className="agent-metrics-message">Select agents to compare their metrics.</p>;
    }
    if (status === 'failed') {
      return <p className="agent-metrics-message">Agent metrics could not be loaded.</p>;
    }
    if (!metrics) {
      return <p className="agent-metrics-message">Loading agent metrics…</p>;
    }
    if (metrics.agents.length === 0) {
      return (
        <p className="agent-metrics-message">
          {selection === null
            ? 'This organization has no agents yet.'
            : 'Select agents to compare their metrics.'}
        </p>
      );
    }
    if (metrics.series === null) {
      return <p className="agent-metrics-message">Execution metrics are unavailable.</p>;
    }

    const series = metrics.series;

    return (
      <>
        <ul className="agent-metrics-legend" aria-label="Agents">
          {shownAgents.map((agent) => {
            const totals = OUTCOMES.map(
              ({ metric, outcome }) =>
                `${formatCount(total(series[metric]?.[agent.id]))} ${outcome}`,
            );
            return (
              <li key={agent.id}>
                <span className={lineKeyClass(`series-${slots.get(agent.id) ?? 1}`)} />
                <span>{agent.name}</span>
                <span className="agent-metrics-total">{totals.join(' · ')}</span>
              </li>
            );
          })}
        </ul>

        <div className={`agent-metrics-grid${status === 'loading' ? ' is-refreshing' : ''}`}>
          {METRIC_CHARTS.map((chart) => {
            const lines: LineSeries[] = shownAgents.map((agent) => ({
              id: agent.id,
              name: agent.name,
              color: `series-${slots.get(agent.id) ?? 1}`,
              values: series[chart.metric]?.[agent.id] ?? [],
            }));

            return (
              <LineChart
                key={chart.metric}
                title={chart.title}
                buckets={metrics.buckets}
                stepSeconds={metrics.step_seconds}
                series={lines}
                formatValue={chart.formatValue}
                unit={chart.unit}
                integerTicks={chart.integerTicks}
                emptyMessage="No data in this period"
              />
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="agent-metrics">
      <div className="agent-metrics-filters">
        <DateRangeSelector storageKey="org-agent" onChange={handlePeriodChange} />
        <div className="agent-metrics-agent-select">
          <CustomSelect
            id="agent-metrics-agents"
            options={options}
            value={selectedIds}
            multiSelect
            maxItems={MAX_AGENTS}
            placeholder="Select agents to compare"
            onChange={handleSelectionChange}
          />
        </div>
      </div>
      {renderBody()}
    </div>
  );
}
